import React, { useState } from 'react';
import { TrendingUp, AlertTriangle, Lightbulb, BarChart3, FileText, X } from 'lucide-react';
import toast from 'react-hot-toast';
import TranscriptRequestGenerator from '../TranscriptRequestGenerator';

const ProfileSidebar = ({ completeness, aiConfidence, inconsistencies, impactForecasts, profileData, onFieldEdit, onOpenDiagnostics }) => {
  // Defensive validation of props
  const safeAiConfidence = typeof aiConfidence === 'number' ? aiConfidence : 0;
  const safeInconsistencies = Array.isArray(inconsistencies) ? inconsistencies : [];
  const safeImpactForecasts = Array.isArray(impactForecasts) ? impactForecasts : [];

  // Transcript Request modal state
  const [showTranscriptRequest, setShowTranscriptRequest] = useState(false);
  
  const handleForecastClick = (forecast) => {
    // Check if forecast has the expected structure
    if (!forecast || !forecast.field) {
      console.error(`Invalid forecast object: ${JSON.stringify(forecast)}`);
      return;
    }
    
    // Show toast notification if toast is available
    if (toast && toast.success) {
      toast.success(`Opening editor for ${forecast.field}`);
    }
    
    // For assessment, just scroll to assessment section
    if (forecast.field === 'assessment') {
      const assessmentElement = document.getElementById('assessment-section');
      if (assessmentElement) {
        assessmentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Navigate to the field and focus it
    if (onOpenDiagnostics && typeof onOpenDiagnostics === 'function') {
      // Close any open modals first
      const event = new CustomEvent('navigateToField', { 
        detail: { field: forecast.field, section: forecast.section } 
      });
      window.dispatchEvent(event);
    }
    
    // Use the same navigation as Profile Strength buttons
    const sectionElement = document.getElementById(forecast.section);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Trigger edit mode for the field
      setTimeout(() => {
        const fieldElement = sectionElement.querySelector(`[data-field="${forecast.field}"]`);
        if (fieldElement) {
          fieldElement.click();
        }
      }, 500);
    }
  };

  const handlePromptClick = (field) => {
    // Show toast notification if toast is available
    if (toast && toast.success) {
      toast.success(`Opening editor for ${field}`);
    }
    
    // Map field to appropriate section for Smart Prompts
    const fieldToSection = {
      curriculum_rigor: 'academics',
      hs_competitiveness: 'academics', 
      time_commitment: 'activities',
      financial_aid_needed: 'financial'
    };
    const section = fieldToSection[field] || 'academics';
    
    // Call onFieldEdit with proper parameters
    if (onFieldEdit) {
      onFieldEdit(field, null, section);
    }
  };

  const getSmartPrompts = () => {
    const prompts = [];
    
    // ONLY show data quality issues and inconsistencies
    // Skip field population prompts since that's covered by Impact Forecasts
    
    // Check for inconsistencies - these are data quality issues that need attention
    if (safeInconsistencies.length > 0) {
      safeInconsistencies.slice(0, 3).forEach(issue => {
        prompts.push({
          text: issue.message,
          action: 'Verify Data',
          field: issue.field,
          type: 'warning'
        });
      });
    }
    
    // Add contextual tips or guidance (not field population)
    // Only show if we don't have many inconsistencies
    if (prompts.length < 2) {
      // Check for specific scenarios that need guidance
      if (completeness?.sections?.testScores?.percentage > 0 && completeness?.sections?.testScores?.percentage < 50) {
        prompts.push({
          text: 'Incomplete test scores may affect college matching accuracy. Consider updating with latest scores.',
          action: 'Review Test Scores',
          field: 'sat_total',
          type: 'suggestion'
        });
      }
      
      if (completeness?.sections?.activities?.percentage > 80 && !profileData?.academic?.time_commitment) {
        prompts.push({
          text: 'Your activities look great! Adding time commitments would help demonstrate leadership depth.',
          action: 'Quantify Impact',
          field: 'time_commitment',
          type: 'suggestion'
        });
      }
    }
    
    return prompts;
  };

  const smartPrompts = getSmartPrompts();

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      {/* Profile Strength Overview */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 bg-stone-200 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 sm:w-5 h-4 sm:h-5 text-slate-700" />
            <div className="text-sm sm:text-base font-semibold text-slate-800">Profile Strength</div>
          </div>
        </div>
        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
            <div 
              className="text-center p-2 sm:p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => {
                console.log('Complete Fields clicked');
                if (typeof onOpenDiagnostics === 'function') {
                  onOpenDiagnostics('missing');
                }
              }}
              title="Click to see all fields"
            >
              <span className="text-lg sm:text-xl font-bold text-slate-800 block">
                {completeness?.completedFields || 0}/{completeness?.totalFields || 0}
              </span>
              <span className="text-xs text-slate-500 mt-0.5">Complete Fields</span>
            </div>
            <div 
              className="text-center p-2 sm:p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => {
                console.log('Missing Optional clicked');
                if (typeof onOpenDiagnostics === 'function') {
                  onOpenDiagnostics('missing');
                }
              }}
              title="Click to see missing fields"
            >
              <span className="text-lg sm:text-xl font-bold text-slate-800 block">
                {completeness?.missingFields?.optional?.length || 0}
              </span>
              <span className="text-xs text-slate-500 mt-0.5">Missing Optional</span>
            </div>
            <div 
              className="text-center p-2 sm:p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => {
                console.log('AI Confidence clicked');
                if (typeof onOpenDiagnostics === 'function') {
                  onOpenDiagnostics('confidence');
                }
              }}
              title="Click to see confidence breakdown"
            >
              <span className="text-lg sm:text-xl font-bold text-slate-800 block">
                {safeAiConfidence}%
              </span>
              <span className="text-xs text-slate-500 mt-0.5">AI Confidence</span>
            </div>
            <div 
              className="text-center p-2 sm:p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              onClick={() => {
                console.log('Inconsistencies clicked');
                if (typeof onOpenDiagnostics === 'function') {
                  onOpenDiagnostics('inconsistencies');
                }
              }}
              title="Click to review inconsistencies"
            >
              <span className={`text-lg sm:text-xl font-bold block ${
                safeInconsistencies.length > 0 
                  ? safeInconsistencies.some(i => i.type === 'error') 
                    ? 'text-red-600' 
                    : 'text-amber-600'
                  : 'text-slate-800'
              }`}>
                {safeInconsistencies.length}
              </span>
              <span className="text-xs text-slate-500 mt-0.5">Inconsistencies</span>
            </div>
          </div>
          
          {/* Overall Progress Bar */}
          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-medium text-slate-600">Overall Completion</span>
              <span className="text-xs font-bold text-slate-800">{completeness?.overall || 0}%</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  (completeness?.overall || 0) >= 80 ? 'bg-teal-500' :
                  (completeness?.overall || 0) >= 60 ? 'bg-gold-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${completeness?.overall || 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* AI Confidence Impact - Redesigned */}
      {safeImpactForecasts && safeImpactForecasts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-700" />
              <div className="text-sm sm:text-base font-semibold text-slate-800">AI Confidence Impact</div>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            {/* AI Confidence Projection */}
            <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg">
              <div className="text-xs font-medium text-slate-600 mb-1">If you complete top 3 items:</div>
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-slate-800">{safeAiConfidence}%</span>
                <span className="text-sm text-slate-500">→</span>
                <span className="text-lg font-bold text-emerald-600">
                  {Math.min(100, Math.round(safeAiConfidence + safeImpactForecasts.slice(0, 3).reduce((sum, f) => sum + f.impact, 0)))}%
                </span>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-1 rounded">
                  +{Math.round(safeImpactForecasts.slice(0, 3).reduce((sum, f) => sum + f.impact, 0) * 10) / 10}%
                </span>
              </div>
            </div>
            
            {/* Top Impact Items */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Priority Actions</div>
              {safeImpactForecasts.slice(0, 3).map((forecast, index) => (
                <button
                  key={index}
                  className="w-full flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-indigo-50 transition-all duration-200 text-left group"
                  onClick={() => handleForecastClick(forecast)}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span className="w-4 sm:w-5 h-4 sm:h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center group-hover:bg-indigo-200">
                      {index + 1}
                    </span>
                    <span className="text-sm text-slate-700 group-hover:text-indigo-700">
                      {forecast.action}
                    </span>
                  </div>
                  <span className="bg-indigo-500 group-hover:bg-indigo-600 text-white px-2 py-0.5 rounded text-xs font-semibold">
                    +{forecast.impact}%
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Smart Contextual Prompts */}
      {smartPrompts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 sm:w-5 h-4 sm:h-5 text-slate-700" />
              <div className="text-sm sm:text-base font-semibold text-slate-800">Smart Prompts</div>
            </div>
          </div>
          <div className="p-4 sm:p-5 space-y-3">
            {smartPrompts.map((prompt, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  prompt.type === 'warning' 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-violet-50 border-violet-200'
                }`}
              >
                <div className={`text-sm mb-2 ${
                  prompt.type === 'warning' ? 'text-amber-700' : 'text-violet-700'
                }`}>
                  {prompt.type === 'warning' && <AlertTriangle className="w-4 h-4 inline mr-1" />}
                  {prompt.text}
                </div>
                <button
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    prompt.type === 'warning'
                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                      : 'bg-violet-600 text-white hover:bg-violet-700'
                  }`}
                  onClick={() => handlePromptClick(prompt.field)}
                >
                  {prompt.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Quick Stats */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
          <div className="text-sm sm:text-base font-semibold text-slate-800">Quick Stats</div>
        </div>
        <div className="p-4 sm:p-5 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Critical Fields Missing</span>
            <span className="text-sm font-semibold text-slate-800">
              {completeness?.missingFields?.critical?.length || 0}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Important Fields Missing</span>
            <span className="text-sm font-semibold text-slate-800">
              {completeness?.missingFields?.important?.length || 0}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Data Quality Score</span>
            <span className="text-sm font-semibold text-slate-800">
              {Math.max(0, 100 - (safeInconsistencies.length * 5))}%
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Profile Categories</span>
            <span className="text-sm font-semibold text-slate-800">8</span>
          </div>
        </div>
      </div>

      {/* Transcript Request Tool */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
          <div className="text-sm sm:text-base font-semibold text-slate-800">College Tools</div>
        </div>
        <div className="p-4 sm:p-5">
          <button
            onClick={() => setShowTranscriptRequest(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-magenta-500 to-magenta-600 text-white rounded-lg font-semibold transition-all hover:shadow-md border-2 border-slate-900"
          >
            <FileText className="w-4 h-4" />
            Generate Transcript Request
          </button>
          <p className="text-xs text-slate-600 mt-2">
            Create a professional email to request your transcript from your counselor
          </p>
        </div>
      </div>

      {/* Transcript Request Modal */}
      {showTranscriptRequest && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Transcript Request Generator</h2>
              <button
                onClick={() => setShowTranscriptRequest(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
              <TranscriptRequestGenerator
                studentProfile={profileData}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSidebar;